import React from 'react';
import type { PtDirectoryPatient } from '../../../patients-list/types';
import { ptFormatMoney, ptPatientDisplayName, ptRollupHealth } from '../../../patients-list/utils';
import { Icon } from '../../Icon/Icon';
import { PatientDirectoryFlags } from './PatientDirectoryFlags';
import { PatientDirectoryPhoto } from './PatientDirectoryPhoto';
import { PatientPrepRow } from './PatientPrepRow';

export interface PatientDirectoryCardProps {
  patient: PtDirectoryPatient;
  showPhotos?: boolean;
  onSelect?: (patient: PtDirectoryPatient) => void;
}

export function PatientDirectoryCard({
  patient,
  showPhotos = true,
  onSelect,
}: PatientDirectoryCardProps) {
  const health = ptRollupHealth(patient);
  const name = ptPatientDisplayName(patient);
  const hasValue = patient.todayValue > 0;

  return (
    <button
      type="button"
      className="pt-card w-full text-left"
      onClick={() => onSelect?.(patient)}
    >
      <div className="flex items-start gap-[13px]">
        <PatientDirectoryPhoto patient={patient} health={health} showPhoto={showPhotos} />
        <div className="min-w-0 flex-1 pt-px">
          <div className="truncate font-[var(--font-display)] text-base font-semibold leading-[1.2] tracking-[-0.02em] text-[var(--ink-0)]">
            {name}
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <span className="shrink-0 whitespace-nowrap font-[var(--font-mono)] text-[11.5px] font-semibold text-[var(--ink-0)]">
              {patient.apptTime}
            </span>
            <span className="h-[3px] w-[3px] shrink-0 rounded-full bg-[var(--ink-4)]" />
            <span className="truncate text-[11.5px] text-[var(--ink-3)]">{patient.provider}</span>
          </div>
        </div>
      </div>

      {patient.procedure ? (
        <div className="mt-3 flex items-baseline gap-2 rounded-[var(--r-md)] bg-[var(--cream-0)] px-[11px] py-2">
          <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-[var(--ink-1)]">
            {patient.procedure}
          </span>
          {hasValue ? (
            <span className="shrink-0 font-[var(--font-mono)] text-[12.5px] font-bold text-[var(--success)]">
              {ptFormatMoney(patient.todayValue)}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="mt-3 flex flex-col gap-[5px]">
        <div
          className="flex items-center gap-2 text-xs"
          style={{ color: patient.phone ? 'var(--ink-2)' : 'var(--ink-4)' }}
        >
          <Icon kind="phone" size={12} stroke={1.8} className="shrink-0 text-[var(--ink-4)]" />
          <span className={patient.phone ? 'whitespace-nowrap font-[var(--font-mono)]' : ''}>
            {patient.phone || 'No phone'}
          </span>
        </div>
        <div
          className="flex items-center gap-2 text-xs"
          style={{ color: patient.email ? 'var(--ink-2)' : 'var(--ink-4)' }}
        >
          <Icon kind="mail" size={12} stroke={1.8} className="shrink-0 text-[var(--ink-4)]" />
          <span className={`min-w-0 truncate ${patient.email ? '' : 'italic'}`}>
            {patient.email || 'No email'}
          </span>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-px border-t border-[var(--cream-2)] pt-[11px]">
        <PatientPrepRow label="Confirmed appointment" on={patient.confirmed} />
        <PatientPrepRow label="Insurance verified" on={patient.insurance} />
        <PatientPrepRow label="Forms completed" on={patient.forms} />
        {patient.premedicate ? (
          <PatientPrepRow
            label="Premedicate ready"
            on={Boolean(patient.alert)}
            critical={!patient.alert}
          />
        ) : null}
      </div>

      <PatientDirectoryFlags patient={patient} />
    </button>
  );
}
